import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  sharedRelated,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
} from "./learnShared";
import type { LearnPageData } from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "Does Blue Cross Blue Shield cover SAINT® TMS?",
    answer:
      "BCBS plans vary. BCBS is a federation of independent plans, so coverage depends on your specific plan and employer. Some BCBS plans may cover SAINT®'s Category III CPT codes; others may not. Prior authorization may be required. Neuro Wellness can verify your specific BCBS benefits at no cost.",
  },
  {
    question: "Do all BCBS plans cover the same things?",
    answer:
      "No. BCBS plans are independently operated by state-licensed companies. Coverage, networks, and medical policies differ between plans. Employer plans differ too. The Federal Employee Program (FEP) has its own benefits. Always verify your specific plan.",
  },
  {
    question: "Does the BCBS Federal Employee Program cover SAINT®?",
    answer:
      "The BCBS Federal Employee Program (FEP) has its own benefits, which may differ from state BCBS plans. FEP may have different coverage for SAINT®'s Category III codes. Neuro Wellness can verify FEP benefits. Contact us with your FEP plan details.",
  },
  {
    question: "Is prior authorization required for SAINT® under BCBS?",
    answer:
      "Prior authorization may be required. If it is, the plan reviews your medical records to determine whether treatment appears medically necessary. Neuro Wellness submits prior authorization when required and can assist with the process.",
  },
  {
    question:
      "How are accelerated protocols handled differently than standard TMS?",
    answer:
      "Standard TMS is billed under established CPT codes (90867–90869) and is broadly covered. SAINT® uses Category III CPT codes (0889T–0892T), which plans may handle differently — some cover them, some treat them as investigational. This is why benefits verification is essential.",
  },
  {
    question: "What information is needed to verify my BCBS benefits?",
    answer:
      "We need your full name, date of birth, insurance member ID, group number, and the phone number on the back of your card. With this, we contact BCBS to verify whether SAINT®'s codes are covered, what your cost-sharing is, and whether prior authorization is required.",
  },
];

export const pageBcbsSaint: LearnPageData = {
  slug: "blue-cross-blue-shield-saint-tms",
  title: "Does BCBS Cover SAINT® TMS? | Neuro Wellness",
  description:
    "Does Blue Cross Blue Shield cover SAINT® TMS? BCBS plans vary, employer plans differ, FEP benefits, prior authorization, accelerated vs standard TMS, and benefit verification.",
  canonical: "/insurance/blue-cross-blue-shield-saint-tms",
  h1: "Does Blue Cross Blue Shield Cover SAINT® TMS?",
  breadcrumbLabel: "BCBS & SAINT® TMS",
  crumbs: baseCrumbs("BCBS & SAINT® TMS"),
  shortAnswer:
    "Blue Cross Blue Shield (BCBS) is a federation of independent plans, so whether BCBS covers SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) depends on your specific plan and employer. Some BCBS plans may cover SAINT®'s Category III CPT codes; others may not. Prior authorization may be required, and the Federal Employee Program (FEP) has its own benefits. Neuro Wellness TMS Centers of America can verify your specific BCBS benefits at no cost. This page does not make universal coverage claims — coverage varies by plan and individual circumstances.",
  keyTakeaways: [
    "BCBS plans vary — there is no universal answer.",
    "Coverage depends on your specific plan and employer.",
    "The Federal Employee Program has its own benefits.",
    "Prior authorization may be required.",
    "Neuro Wellness verifies your BCBS benefits at no cost.",
  ],

  sections: [
    {
      heading: "BCBS Plans Vary",
      body: (
        <>
          {P(
            "Blue Cross Blue Shield is a federation of independent, state-licensed companies. Coverage, networks, and medical policies differ between plans. A BCBS plan in one state may cover something that a plan in another state does not. There is no single BCBS policy for SAINT®.",
          )}
        </>
      ),
    },
    {
      heading: "Employer Plans Differ",
      body: (
        <>
          {P(
            "Even within the same BCBS company, employer plans differ. Employers choose different coverage levels, networks, and deductibles. Two people with 'BCBS' may have very different benefits. Always verify your specific plan using your member ID and group number.",
          )}
        </>
      ),
    },
    {
      heading: "Federal Employee Program Benefits",
      body: (
        <>
          {P(
            "The BCBS Federal Employee Program (FEP) has its own benefits, which may differ from state BCBS plans. FEP may have different coverage for SAINT®'s Category III codes. Neuro Wellness can verify FEP benefits — contact us with your FEP plan details.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/federal-employee-saint-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                SAINT® for federal employees
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Prior Authorization",
      body: (
        <>
          {P(
            "Prior authorization may be required for SAINT® under BCBS. This is a clinical review in which the plan decides, based on your records, whether treatment appears medically necessary. If required and not obtained, the claim may be denied. Neuro Wellness submits prior authorization when required.",
          )}
        </>
      ),
    },
    {
      heading: "Accelerated Protocols vs Standard TMS",
      body: (
        <>
          {P(
            "Standard TMS is billed under established CPT codes (90867–90869) and is broadly covered by BCBS when medical-necessity criteria are met. SAINT® uses Category III CPT codes (0889T–0892T), which BCBS plans may handle differently — some cover them, some treat them as investigational. This is why benefits verification is essential.",
          )}
          {P(
            <>
              Learn more about{" "}
              <a
                href="/learn/does-insurance-cover-saint-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                insurance coverage for SAINT®
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Neuro Wellness Can Assist with Benefit Verification",
      body: (
        <>
          {P(
            "Neuro Wellness verifies your BCBS benefits at no cost. We contact BCBS to confirm whether SAINT®'s codes are covered, what your cost-sharing is, and whether prior authorization is required. We provide a written estimate after verification.",
          )}
          {P(
            <>
              To verify your benefits,{" "}
              <a
                href="/contactus"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                contact our team
              </a>{" "}
              or call 954-827-9793.
            </>,
          )}
        </>
      ),
    },
    {
      heading: "What Information We May Need to Verify Your Benefits",
      body: (
        <>
          <ul className="mt-4 space-y-2">
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Full name and date of birth
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Insurance member ID and group number
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              The phone number on the back of your card
            </li>
            <li className="relative pl-5 leading-relaxed text-foreground before:absolute before:left-0 before:top-2.5 before:h-1.5 before:w-1.5 before:rounded-full before:bg-accent">
              Whether this is an employer plan, FEP, or marketplace plan
            </li>
          </ul>
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "American Medical Association. CPT Category III Codes 0889T–0892T (SAINT / accelerated TMS).",
      url: "https://www.ama-assn.org/practice-management/cpt",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/insurance/blue-cross-blue-shield-saint-tms#webpage`,
        name: "Does Blue Cross Blue Shield Cover SAINT® TMS?",
        url: `${SITE_URL}/insurance/blue-cross-blue-shield-saint-tms`,
        description:
          "Does BCBS cover SAINT® TMS? Plans vary, employer plans differ, FEP, prior authorization, and benefit verification.",
      }),
      orgNode,
      faqJsonld(
        faqs,
        `${SITE_URL}/insurance/blue-cross-blue-shield-saint-tms#faq`,
      ),
      breadcrumbJsonld(baseCrumbs("BCBS & SAINT® TMS")),
    ],
  },
  relatedLinks: [
    ...sharedRelated,
    {
      title: "Does Insurance Cover SAINT® TMS?",
      href: "/learn/does-insurance-cover-saint-tms",
      description: "General insurance coverage for SAINT®",
    },
    {
      title: "SAINT® TMS Cost",
      href: "/learn/saint-tms-cost",
      description: "Pricing components and self-pay options",
    },
  ],
};
